<template>
  <!-- ── Icon mode ─────────────────────────────────────────────────────── -->
  <template v-if="mode === 'icon'">
    <div
      class="device-icon-pin light-icon-pin"
      :class="[sizeClass, isOn ? 'device-active--light' : '']"
      :title="device.deviceName"
      :style="accentColor ? { '--device-color-light': accentColor } : {}">
      <!-- The status ring carries the brightness; inside it the device's own
           thumbnail when it has one, else the drawn bulb (P13). The picture is
           read through the S3-key cache (`getDefaultThumbnailUrl`), not off
           `device.thumbnailUrl` raw: that field is `null` in a cloud-compact row
           and a hub-reach presigned URL after any device PATCH, so a pin lost its
           picture (owner, 2026-10-20: *"the device widgets lose thumb bg image
           when selected in editor.. sometimes"*). A link that still fails to load
           falls back to the bulb instead of an empty circle. -->
      <ControlStatusRing :value="ringValue" :accent="accentColor || 'var(--device-color-light)'">
        <div v-if="iconThumbUrl && !iconThumbFailed" class="device-icon-pin__thumb">
          <img
            :src="iconThumbUrl"
            :alt="device.deviceName"
            class="device-icon-pin__img"
            :class="{ 'device-icon-pin__img--grayscale': !isOn, 'device-icon-pin__img--lit': isOn }"
            draggable="false"
            @error="iconThumbFailed = true" />
        </div>
        <!-- The drawn bulb lights up with the light's own colour -->
        <ControlIcon
          v-else
          name="bulb"
          :size="pinIconSize"
          :style="isOn ? { color: accentColor || 'var(--device-color-light)' } : {}" />
      </ControlStatusRing>
      <div
        v-if="isOn"
        class="device-icon-pin__glow"
        :style="
          accentColor
            ? { background: `radial-gradient(circle, ${accentColor}8C 0%, ${accentColor}38 45%, transparent 72%)` }
            : {}
        "></div>
    </div>
  </template>

  <!-- ── Card mode ───────────────────────────────────────────────────── -->
  <template v-else-if="mode === 'card'">
    <!-- Card face (the lights page's grid): the card the page used to draw
         itself, drawn here instead so there is one light card in the app. The
         picture, the name and the reading are always there; the brightness
         slider only appears at the largest size, where the cell is tall
         enough for it. -->
    <DeviceCardShell
      :size="cardSize ?? 'min'"
      :name="device.deviceName"
      :title="device.deviceName"
      :active="isOn"
      :accent="accentColor"
      fill
      class="light-card-face"
      :class="isOn ? 'light-on' : 'light-off'"
      :style="accentColor ? { '--device-color-light': accentColor } : undefined"
      @click="buttonTap.onClick"
      @pointerdown="buttonTap.onPointerDown"
      @pointermove="buttonTap.onPointerMove"
      @pointerup="buttonTap.onPointerUp"
      @pointerleave="buttonTap.onPointerLeave"
      @pointercancel="buttonTap.onPointerCancel"
      @contextmenu.prevent="emit('open-control-modal')">
      <template #media>
        <img
          v-if="iconThumbUrl"
          :src="iconThumbUrl"
          alt=""
          class="light-card-face__img"
          :class="{ 'light-card-face__img--off': !isOn }"
          draggable="false" />
        <span v-else class="material-symbols-outlined light-card-face__glyph">{{ device.icon || 'lightbulb' }}</span>
      </template>

      <template v-if="cardSize !== 'min'" #value>
        <span class="light-card-face__value">
          {{ hasBrightness ? brightnessLabel : isOn ? tt('On', 'Vklop') : tt('Off', 'Izklop') }}
        </span>
      </template>

      <template v-if="cardSize === 'max' && hasBrightness" #control>
        <div class="light-dim-controls light-card-face__dim">
          <div
            @pointerdown.stop="onDimHoldStart(0, $event)"
            @mousedown.stop="onDimHoldStart(0)"
            @pointerup.stop="onDimHoldEnd"
            @mouseup.stop="onDimHoldEnd"
            @pointerleave="onDimHoldEnd"
            @mouseleave="onDimHoldEnd"
            @pointercancel="onDimHoldEnd"
            class="light-dim-button"
            :title="tt('Dim down (hold)', 'Zatemnitev (drži)')"
            :aria-label="tt('Dim down', 'Zatemnitev')">
            <span class="material-symbols-outlined">remove</span>
          </div>
          <div
            class="device-button-bright-bar light-dim-bar"
            @pointerdown.stop="onBrightBarDown"
            @pointermove.stop="onBrightBarMove"
            @pointerup.stop="onBrightBarUp"
            @pointercancel.stop="onBrightBarUp"
            @lostpointercapture.stop="onBrightBarUp">
            <div
              class="device-button-bright-fill"
              :class="{
                'device-button-bright-fill--off': !isOn,
                'device-button-bright-fill--dragging': activeDrag === 'brightness',
              }"
              :style="{ width: brightnessFillPct }"></div>
          </div>
          <div
            @pointerdown.stop="onDimHoldStart(1, $event)"
            @mousedown.stop="onDimHoldStart(1)"
            @pointerup.stop="onDimHoldEnd"
            @mouseup.stop="onDimHoldEnd"
            @pointerleave="onDimHoldEnd"
            @mouseleave="onDimHoldEnd"
            @pointercancel="onDimHoldEnd"
            class="light-dim-button"
            :title="tt('Dim up (hold)', 'Osvetlitev (drži)')"
            :aria-label="tt('Dim up', 'Osvetlitev')">
            <span class="material-symbols-outlined">add</span>
          </div>
        </div>
      </template>
    </DeviceCardShell>
  </template>

  <!-- ── Grid mode ───────────────────────────────────────────────────── -->
  <template v-else-if="mode === 'grid'">
    <!-- Expanded: colSpan >= 2 — icon + name + brightness bar -->
    <template v-if="(colSpan ?? 1) >= 2">
      <button
        class="w-full h-full flex flex-col justify-between p-2 rounded-xl outline-none select-none touch-pan-y active:scale-[0.98] transition-transform"
        :class="isOn ? 'bg-yellow-400/15 border border-yellow-400/30' : 'bg-white/5 border border-white/10'"
        @pointerdown="onPointerDown"
        @pointermove="onPointerMove"
        @pointerup="onPointerUp"
        @pointerleave="onPointerLeave"
        @pointercancel="onPointerCancel"
        @click="onClick"
        @contextmenu.prevent>
        <div class="flex items-center gap-1.5 min-w-0">
          <span
            class="material-symbols-outlined text-2xl transition-colors"
            :class="isOn ? 'text-yellow-400' : 'text-gray-400'"
            >{{ device.icon || 'lightbulb' }}</span
          >
          <span class="text-sm font-medium text-white truncate flex-1">{{ displayName }}</span>
          <span class="text-xs font-semibold shrink-0" :class="isOn ? 'text-yellow-400' : 'text-gray-500'">
            {{ brightnessLabel }}
          </span>
        </div>
        <div
          class="w-full h-6 bg-white/10 rounded-full overflow-hidden cursor-pointer"
          @pointerdown.stop="onBrightBarDown"
          @pointermove.stop="onBrightBarMove"
          @pointerup.stop="onBrightBarUp"
          @pointercancel.stop="onBrightBarUp"
          @lostpointercapture.stop="onBrightBarUp">
          <div
            class="h-full bg-yellow-400 rounded-full transition-all duration-150"
            :class="{ 'transition-none': activeDrag === 'brightness' }"
            :style="{ width: brightnessFillPct }"></div>
        </div>
      </button>
    </template>
    <!-- Minimal: 1-wide -->
    <template v-else>
      <DeviceGridCell
        :icon="device.icon || 'lightbulb'"
        :name="device.deviceName"
        :is-on="isOn"
        active-color="yellow-400"
        @toggle="setProperty(device, 'onOff', !isOn)"
        @long-press="emit('open-control-modal')" />
    </template>
  </template>

  <!-- ── Control mode ──────────────────────────────────────────────────── -->
  <template v-else-if="mode === 'control'">
    <!-- The body of the sheet: the host owns the head and the panel, the body
         owns the hero and the rows (no head, no outer padding — the host's body
         container carries it). Every primitive inside reads `--sheet-accent`,
         which this root sets to the light's own colour while it is lit (the old
         sheet did the same), else leaves to the host's chrome. -->
    <div class="light-sheet" :style="accentColor ? { '--sheet-accent': accentColor } : {}">
      <!-- HERO — the ring dial: tap the knob toggles the power, drag the ring
           sets the brightness (the widget's own 300 ms debounce). A light with
           no brightness keeps the same dial, with no ring fill. -->
      <div class="light-hero">
        <ControlRingDial
          :value="hasBrightness ? fillBrightness : 0"
          :active="isOn"
          :disabled="busy"
          :show-value="hasBrightness"
          :label="hasBrightness ? tt('Brightness', 'Svetlost') : tt('Power', 'Napajanje')"
          @toggle="handleToggle"
          @set-value="setBrightness" />
        <div class="light-hero-caption">
          {{
            hasBrightness
              ? tt('Tap to toggle · drag the ring to dim', 'Dotakni za preklop · povleci obroč za zatemnitev')
              : tt('Tap to toggle', 'Dotakni za preklop')
          }}
        </div>
      </div>

      <!-- Momentary step dimming (a `dim_step` device, e.g. KNX): the
           hold-capable − / + pair. The wiring is this widget's own hold logic,
           verbatim: both pointer and mouse events start it (the handler's own
           guard collapses the pair), the pointer is captured so the button's
           shrink cannot fire pointerleave and cut the hold, and the release
           writes the stop pulse. -->
      <div v-if="hasDimStep" class="light-dimstep">
        <button
          type="button"
          class="light-dimstep-btn"
          :title="tt('Dim down (hold)', 'Zatemnitev (drži)')"
          :aria-label="tt('Dim down', 'Zatemnitev')"
          @pointerdown.stop="onDimHoldStart(0, $event)"
          @mousedown.stop="onDimHoldStart(0)"
          @pointerup.stop="onDimHoldEnd"
          @mouseup.stop="onDimHoldEnd"
          @pointerleave="onDimHoldEnd"
          @mouseleave="onDimHoldEnd"
          @pointercancel="onDimHoldEnd">
          <ControlIcon name="minus" size="1.25em" />
        </button>
        <button
          type="button"
          class="light-dimstep-btn light-dimstep-btn--accent"
          :title="tt('Dim up (hold)', 'Osvetlitev (drži)')"
          :aria-label="tt('Dim up', 'Osvetlitev')"
          @pointerdown.stop="onDimHoldStart(1, $event)"
          @mousedown.stop="onDimHoldStart(1)"
          @pointerup.stop="onDimHoldEnd"
          @mouseup.stop="onDimHoldEnd"
          @pointerleave="onDimHoldEnd"
          @mouseleave="onDimHoldEnd"
          @pointercancel="onDimHoldEnd">
          <ControlIcon name="plus" size="1.25em" />
        </button>
      </div>

      <!-- Brightness presets — the levels at or under the current one carry the
           calmer tint (`mid`), the level the light is at carries the accent. -->
      <div v-if="hasBrightness" class="light-presets">
        <ControlChip
          v-for="pct in BRIGHTNESS_PRESETS"
          :key="pct"
          :mid="pct < (brightness ?? 0)"
          :active="brightness === pct"
          :disabled="busy"
          @click="setBrightness(pct)">
          {{ pct }} %
        </ControlChip>
      </div>

      <!-- Color temperature — the strip is the sketch's (gradient bar, thumb,
           Warm/Cool ends, the value on the right); the pointer handling and the
           settle delay are the widget's own (2000–6500 K). -->
      <div v-if="hasColorTemperature" class="light-row" :class="{ 'light-row--off': !isOn }">
        <div class="light-row-head">
          <span class="light-row-key">{{ tt('Color Temperature', 'Temperatura barve') }}</span>
          <span class="light-row-value">{{ shownColorTemp }} K</span>
        </div>
        <div
          class="light-strip"
          :class="{ 'light-strip--disabled': controlPending, 'light-strip--dragging': activeDrag === 'temp' }"
          style="
            background: linear-gradient(
              to right,
              hsl(25, 100%, 50%),
              hsl(40, 100%, 50%),
              hsl(50, 100%, 50%),
              hsl(45, 100%, 55%),
              hsl(200, 80%, 60%)
            );
          "
          @pointerdown="onTempDown"
          @pointermove="onTempMove"
          @pointerup="onTempUp"
          @pointercancel="onTempUp"
          @lostpointercapture="onTempUp">
          <i class="light-strip-thumb" :style="{ left: `${colorTempPct}%` }"></i>
        </div>
        <div class="light-row-ends">
          <span>{{ tt('Warm', 'Toplo') }}</span>
          <span>{{ tt('Cool', 'Hladno') }}</span>
        </div>
      </div>

      <!-- Color — the hue strip writes through the existing `color` path, its
           thumb carries the current colour; the native picker stays hidden
           behind the palette button for fine-tuning. -->
      <div v-if="hasColor" class="light-row" :class="{ 'light-row--off': !isOn }">
        <div class="light-row-head">
          <span class="light-row-key">{{ tt('Color', 'Barva') }}</span>
          <span class="light-row-value light-row-value--mono">{{ shownColor }}</span>
          <button
            type="button"
            class="light-icon-btn"
            :disabled="controlling"
            :title="tt('Fine-tune color', 'Natančno nastavi barvo')"
            @click="openColorPicker">
            <ControlIcon name="palette" size="1.0625em" />
          </button>
        </div>
        <div
          ref="hueBarRef"
          class="light-strip light-strip--hue"
          :class="{ 'light-strip--disabled': controlPending, 'light-strip--dragging': activeDrag === 'color' }"
          style="
            background: linear-gradient(
              to right,
              hsl(0, 100%, 50%),
              hsl(30, 100%, 50%),
              hsl(60, 100%, 50%),
              hsl(90, 100%, 50%),
              hsl(120, 100%, 50%),
              hsl(150, 100%, 50%),
              hsl(180, 100%, 50%),
              hsl(210, 100%, 50%),
              hsl(240, 100%, 50%),
              hsl(270, 100%, 50%),
              hsl(300, 100%, 50%),
              hsl(330, 100%, 50%),
              hsl(360, 100%, 50%)
            );
          "
          @pointerdown="onHueDown"
          @pointermove="onHueMove"
          @pointerup="onHueUp"
          @pointercancel="onHueUp"
          @lostpointercapture="onHueUp">
          <i
            class="light-strip-thumb light-strip-thumb--tinted"
            :style="{ left: `${colorHuePct}%`, backgroundColor: shownColor, borderColor: shownColor }"></i>
        </div>
        <!-- Hidden native color input (fine-tune) -->
        <input
          ref="colorInputRef"
          type="color"
          :value="shownColor"
          @change="handleColorChange"
          :disabled="controlling"
          class="sr-only" />
      </div>

      <!-- WLED effects / palette / preset — the option lists load through
           `loadWledOptions` exactly as before (its watcher must stay below the
           wled* refs); only the markup is restyled to the sheet's fields. -->
      <div v-if="hasWledEffects" class="light-row light-row--wled" :class="{ 'light-row--off': !isOn }">
        <p v-if="!wledEffects.length" class="light-wled-empty">
          {{
            tt(
              'Effects, palettes and presets appear once the WLED board is online.',
              'Učinki, palete in prednastavitve se prikažejo, ko je WLED plošča povezana.',
            )
          }}
        </p>
        <div v-if="wledEffects.length" class="light-field">
          <label class="light-field-label">{{ tt('Effect', 'Učinek') }}</label>
          <select
            :value="wledEffect"
            :disabled="controlling"
            class="light-select"
            @change="setWledValue('effect', ($event.target as HTMLSelectElement).value)">
            <option v-for="opt in wledEffects" :key="opt" :value="opt">{{ opt }}</option>
          </select>
        </div>
        <div v-if="wledPalettes.length" class="light-field">
          <label class="light-field-label">{{ tt('Palette', 'Paleta') }}</label>
          <select
            :value="wledPalette"
            :disabled="controlling"
            class="light-select"
            @change="setWledValue('palette', ($event.target as HTMLSelectElement).value)">
            <option v-for="opt in wledPalettes" :key="opt" :value="opt">{{ opt }}</option>
          </select>
        </div>
        <div v-if="wledPresets.length" class="light-field">
          <label class="light-field-label">{{ tt('Preset', 'Prednastavitev') }}</label>
          <select
            :value="wledPreset"
            :disabled="controlling"
            class="light-select"
            @change="setWledValue('preset', ($event.target as HTMLSelectElement).value)">
            <option v-if="!wledPresets.includes(wledPreset)" :value="wledPreset || ''">{{ wledPreset || '—' }}</option>
            <option v-for="opt in wledPresets" :key="opt" :value="opt">{{ opt }}</option>
          </select>
        </div>
      </div>

      <!-- Meta — last updated on the left; the activity strip below is the
           SetpointTimeline, kept as-is. -->
      <div v-if="lastUpdated" class="light-meta">
        <span class="light-meta-left">
          <ControlIcon name="clock" size="0.8125rem" />
          {{ tt('Last updated:', 'Zadnja posodobitev:') }} {{ lastUpdated }}
        </span>
      </div>

      <SwitchAutomationPanel
        v-if="lightAutomations.length"
        :automations="lightAutomations"
        @updated="refreshLightAutomations" />

      <!-- Setpoint history timeline -->
      <SetpointTimeline
        :device-values="allDeviceValues"
        :device-id="device.id"
        :accent-color="accentColor || 'rgb(250, 204, 21)'"
        :filter-types="['Setpoint']" />
    </div>
  </template>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, type Ref } from 'vue';
import { formatTime } from '../../utils/formatDate';
import { apiFetch } from '../../services/api';
import { useDeviceValues } from '../../composables/useDeviceValues';
import { useDeviceControl } from '../../composables/useDeviceControl';
import { useGridTap } from '../../composables/useGridTap';
import { useSolar } from '../../composables/useSolar';
import DeviceGridCell from '../panel/DeviceGridCell.vue';
import DeviceCardShell from './DeviceCardShell.vue';
import SetpointTimeline from './SetpointTimeline.vue';
import SwitchAutomationPanel from './SwitchAutomationPanel.vue';
import ControlRingDial from '../device-sheet/ControlRingDial.vue';
import ControlChip from '../device-sheet/ControlChip.vue';
import ControlIcon from '../device-icons/ControlIcon.vue';
import ControlStatusRing from '../device-sheet/ControlStatusRing.vue';
import { useDeviceAutomationsStore } from '../../stores/deviceAutomations';
import {
  buildSwitchAutomationDisplay,
  getSwitchWidgetAutomationSummaries,
  toSunTimesLike,
  type SwitchAutomationDisplay,
} from '../../utils/switchAutomation';
import { getDefaultThumbnailUrl } from '../../composables/useDeviceThumbnail';
import { tt } from '../../utils/consoleText';
import { deviceValuesOf, type WidgetDeviceValue } from './deviceWidgetTypes';
import { wireJsonBag } from '../../utils/wire';
import type { Device, DeviceValue } from '@/types';

interface Props {
  device: Device;
  mode?: 'icon' | 'card' | 'control' | 'grid';
  size?: 'sm' | 'md' | 'lg';
  fluid?: boolean;
  displayMode?: 'full' | 'room';
  colSpan?: number;
  rowSpan?: number;
  /** Set by the pages that grid cards (`DeviceButton`) — `undefined` keeps the
   *  compact button row other surfaces have always drawn. */
  cardSize?: 'min' | 'mid' | 'max';
}

const props = withDefaults(defineProps<Props>(), { mode: 'card', size: 'md', cardSize: undefined });

const emit = defineEmits<{
  toggle: [];
  'open-control-modal': [];
  'view-stream': [];
  'view-details': [];
  'value-update': [valueId: string, newValue: unknown];
}>();

// ── State ────────────────────────────────────────────────────────────────
const colorInputRef = ref<HTMLInputElement | null>(null);
const hueBarRef = ref<HTMLDivElement | null>(null);

/**
 * How long a dragged value must sit still before it is written to the device.
 * Every continuous control (hue, colour temperature, brightness) reads it: the
 * gesture only moves local state and a single write goes out once the pointer
 * pauses — or the moment it is released. Without that pause a fast drag fired a
 * request per `pointermove`, which floods the driver (a Hue bridge drops the
 * connection under that load) and made `controlling` flap, whose disabled
 * styling is the flicker on the sheet.
 */
const DRAG_SETTLE_MS = 250;

/**
 * After a write, the strip shows the value the user set until the device echoes
 * it back — that hides the round-trip so the thumb never snaps to the old
 * reading. If no echo ever arrives (a sleeping or offline light) the held value
 * is dropped after this grace period, so it can never mask a later change.
 */
const OVERLAY_GRACE_MS = 1500;

/** The control a pointer gesture currently owns, or `null` when none is live. */
const activeDrag = ref<'color' | 'temp' | 'brightness' | null>(null);

// True for the WHOLE dim-hold gesture, unlike `controlling` which flips
// true/false around every 150 ms step request — using `controlling` directly
// for disabled-opacity makes the buttons flash. (The old vertical slider's
// drag flag retired with the slider: the ring dial owns its own drag state.)
const adjusting = computed(() => dimHoldActive.value);
// Stays true for the whole gesture — used for disabled opacity so buttons
// don't flash as `controlling` toggles around each 150 ms step request.
const busy = computed(() => controlling.value || adjusting.value || activeDrag.value !== null);
// `controlling` minus the writes a live drag owns: dimming a strip mid-gesture
// is exactly the flicker the settle delay removes.
const controlPending = computed(() => controlling.value && activeDrag.value === null);

const { currentValues, initializeValues } = useDeviceValues(props.device.id);
const { controlling, setProperty, findValueByKeys } = useDeviceControl();
const automationsStore = useDeviceAutomationsStore();
const solar = useSolar();
const nowTick = ref(Date.now());

// Grid-mode cell: the press is judged on the pointer stream (card t_fcaa5f33).
const { onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onPointerLeave, onClick } = useGridTap(
  () => setProperty(props.device, 'onOff', !isOn.value),
  () => emit('open-control-modal'),
);

// Button-mode cell: same tap logic — a tap toggles the light at every card size,
// a hold opens the controls.
const buttonTap = useGridTap(
  () => void handleToggle(),
  () => emit('open-control-modal'),
);

let nowInterval: ReturnType<typeof setInterval> | null = null;

onMounted(() => {
  nowInterval = setInterval(advanceAutomationClock, 1000);

  void automationsStore.fetchDeviceAutomationSummaries(props.device.id);
});

onUnmounted(() => {
  if (nowInterval) {
    clearInterval(nowInterval);
    nowInterval = null;
  }
  if (dimHoldTimer !== null) {
    clearInterval(dimHoldTimer);
    dimHoldTimer = null;
  }
  brightnessSettle.dispose();
  colorSettle.dispose();
  colorTempSettle.dispose();
});

// Normalize: API returns either `deviceValues` or `values` depending on endpoint
const allDeviceValues = computed<WidgetDeviceValue[]>(() => deviceValuesOf(props.device));

watch(
  allDeviceValues,
  (values) => {
    initializeValues(values ?? [], { replace: true });
  },
  { immediate: true },
);

const isOn = computed(() => {
  const key = ['power', 'on_off', 'state'].find((k) => k in currentValues.value);
  if (key) {
    const v = currentValues.value[key];
    return v === true || v === 1 || v === 'on';
  }
  const dv = findValueByKeys(props.device, ['power', 'on_off', 'state']);
  return dv?.currentValue === true || dv?.currentValue === 1 || dv?.currentValue === 'on';
});

/**
 * The device's own picture, resolved through the S3-key blob cache like every
 * other device card — it backs both the card face and the icon pin. The pin
 * used to read `device.thumbnailUrl` raw, which is `null` on a cloud-compact row
 * and a hub-side presigned URL after any device PATCH — either way the picture
 * left the pin and stayed gone until the next full entity refresh.
 */
const iconThumbUrl = computed(() => getDefaultThumbnailUrl(props.device));

// A different picture is a new chance: a link that failed once must not keep
// the bulb for a thumbnail that has since resolved.
const iconThumbFailed = ref(false);
watch(iconThumbUrl, () => {
  iconThumbFailed.value = false;
});

const brightness = computed<number | null>(() => {
  if ('brightness' in currentValues.value) {
    return toFiniteNumber(currentValues.value['brightness']);
  }
  const dv = allDeviceValues.value.find((v) => ['brightness', 'dimming', 'level'].includes(v.valueKey));
  return toFiniteNumber(dv?.currentValue);
});

// Keep the last non-null brightness so a momentary null during a value update
// can't collapse the slider fill to 0 (which reads as a flicker).
const lastBrightness = ref<number | null>(null);
watch(brightness, (v) => {
  if (v !== null && v !== undefined) lastBrightness.value = v;
});
const fillBrightness = computed(() => brightness.value ?? lastBrightness.value ?? (isOn.value ? 100 : 0));

// What the bars and the dial print: the value the user is dragging (or just
// set) first, else what the device reports — same wording as before.
const brightnessLabel = computed(() => {
  const v = brightnessOverlay.held.value ?? brightness.value;
  return v !== null ? `${Math.round(v)}%` : isOn.value ? 'On' : 'Off';
});
const brightnessFillPct = computed(() => {
  const v = brightnessOverlay.held.value ?? brightness.value;
  return v !== null ? `${v}%` : isOn.value ? '100%' : '0%';
});

function toFiniteNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

// Brightness step/range from the device value metadata, with fallbacks (0–100, step 10)
const brightnessValueMeta = computed(() => {
  const dv = allDeviceValues.value.find((v) => ['brightness', 'dimming', 'level'].includes(v.valueKey));
  // The hub stores a value's metadata as JSON; the bag is read by key below.
  return wireJsonBag(dv?.metadata);
});
const brightnessStep = computed(() => {
  const m = brightnessValueMeta.value;
  const step = toFiniteNumber(m?.step ?? m?.deltaValue ?? m?.delta_value);
  return step !== null && step > 0 ? step : 10;
});
const brightnessMin = computed(
  () =>
    toFiniteNumber(
      brightnessValueMeta.value?.minValue ?? brightnessValueMeta.value?.min_value ?? brightnessValueMeta.value?.min,
    ) ?? 0,
);
const brightnessMax = computed(
  () =>
    toFiniteNumber(
      brightnessValueMeta.value?.maxValue ?? brightnessValueMeta.value?.max_value ?? brightnessValueMeta.value?.max,
    ) ?? 100,
);

/** Convert "R,G,B" or [R,G,B] array to "#rrggbb" hex; pass through if already hex */
function rgbValueToHex(val: unknown): string {
  if (!val) return '#FFAA00';
  if (typeof val === 'string' && val.startsWith('#')) return val;
  let parts: number[];
  if (typeof val === 'string') parts = val.split(',').map(Number);
  else if (Array.isArray(val)) parts = val.map(Number);
  else return '#FFAA00';
  const [r, g, b] = parts;
  if (isNaN(r) || isNaN(g) || isNaN(b)) return '#FFAA00';
  return '#' + [r, g, b].map((x) => Math.round(x).toString(16).padStart(2, '0')).join('');
}

const color = computed(() => {
  const key = ['color', 'rgb'].find((k) => k in currentValues.value);
  const raw = key ? currentValues.value[key] : findValueByKeys(props.device, ['color', 'rgb'])?.currentValue;
  return rgbValueToHex(raw);
});

const colorTemperature = computed<number>(() => {
  if ('color_temp' in currentValues.value) return toFiniteNumber(currentValues.value['color_temp']) ?? 4000;
  return (
    toFiniteNumber(findValueByKeys(props.device, ['color_temp', 'color_temperature', 'temperature'])?.currentValue) ??
    4000
  );
});

/**
 * An optimistic overlay for one dragged value: `hold` shows a value before the
 * device echoes it, and it is released the moment the echo matches (or after
 * `OVERLAY_GRACE_MS`, so a device that never answers cannot pin a stale value
 * over a later change). This is what keeps a strip's thumb under the finger
 * instead of jumping back to the previous reading on every round-trip.
 */
function createOverlay<T>(reported: Ref<T | null>, isEcho: (held: T, value: T) => boolean) {
  const held = ref(null) as Ref<T | null>;
  let graceTimer: number | null = null;

  function release() {
    if (graceTimer !== null) {
      clearTimeout(graceTimer);
      graceTimer = null;
    }
    held.value = null;
  }

  function hold(value: T) {
    held.value = value;
    if (graceTimer !== null) clearTimeout(graceTimer);
    graceTimer = window.setTimeout(release, OVERLAY_GRACE_MS);
  }

  watch(reported, (value) => {
    if (value !== null && held.value !== null && isEcho(held.value, value)) release();
  });

  return { held, hold, release };
}

const colorOverlay = createOverlay<string>(color, (held, value) => held.toLowerCase() === value.toLowerCase());
const colorTempOverlay = createOverlay<number>(
  colorTemperature,
  (held, value) => Math.round(held) === Math.round(value),
);
const brightnessOverlay = createOverlay<number>(brightness, (held, value) => Math.round(held) === Math.round(value));

// Feature flags
const hasBrightness = computed(() => {
  if (Object.keys(currentValues.value).length > 0)
    return ['brightness', 'dimming', 'level'].some((k) => k in currentValues.value);
  return !!findValueByKeys(props.device, ['brightness', 'dimming', 'level']);
});
const hasColorTemperature = computed(() => {
  if (Object.keys(currentValues.value).length > 0)
    return ['color_temp', 'color_temperature'].some((k) => k in currentValues.value);
  return !!findValueByTrait(props.device, 'light_temperature');
});
const hasColor = computed(() => {
  if (Object.keys(currentValues.value).length > 0) return ['color', 'rgb'].some((k) => k in currentValues.value);
  return !!findValueByTrait(props.device, 'light_color');
});

// ── WLED effect / palette / preset ──────────────────────────────────────────
// WLED lights expose these as string values whose options are board-specific.
// Load the board lists from the WLED integration and let the user pick.
const hasWledEffects = computed(() => {
  if (Object.keys(currentValues.value).length > 0) return 'effect' in currentValues.value;
  return !!findValueByKeys(props.device, ['effect']);
});
const wledEffects = ref<string[]>([]);
const wledPalettes = ref<string[]>([]);
const wledPresets = ref<string[]>([]);
const wledOptionsLoaded = ref(false);

async function loadWledOptions() {
  if (wledOptionsLoaded.value) return;
  const integrationId = props.device.integrationId;
  if (!integrationId) return;
  try {
    const data = await apiFetch<{
      effects?: string[];
      palettes?: string[];
      presets?: Record<string, string>;
    }>(`/integrations/${integrationId}/wled/segments`);
    wledEffects.value = data.effects ?? [];
    wledPalettes.value = data.palettes ?? [];
    wledPresets.value = Object.values(data.presets ?? {});
    wledOptionsLoaded.value = true;
  } catch {
    // Board unreachable — keep lists empty; they are reloaded on next mount.
  }
}

// Load the board's effect/palette/preset lists once the WLED values are known.
// NOTE: this watcher must live AFTER the wled* refs above — the original code
// called loadWledOptions() from the earlier immediate watcher, which ran
// before `wledOptionsLoaded` was initialized (temporal-dead-zone crash) and
// left the dropdown lists permanently empty.
watch(
  allDeviceValues,
  (values) => {
    if ((values ?? []).some((v) => v.valueKey === 'effect')) {
      void loadWledOptions();
    }
  },
  { immediate: true },
);

const wledEffect = computed(() =>
  'effect' in currentValues.value
    ? String(currentValues.value['effect'] ?? '')
    : String(findValueByKeys(props.device, ['effect'])?.currentValue ?? ''),
);
const wledPalette = computed(() =>
  'palette' in currentValues.value
    ? String(currentValues.value['palette'] ?? '')
    : String(findValueByKeys(props.device, ['palette'])?.currentValue ?? ''),
);
const wledPreset = computed(() =>
  'preset' in currentValues.value
    ? String(currentValues.value['preset'] ?? '')
    : String(findValueByKeys(props.device, ['preset'])?.currentValue ?? ''),
);

async function setWledValue(key: 'effect' | 'palette' | 'preset', value: string) {
  if (!value) return;
  await setProperty(props.device, key, value);
}

// When the light is on and has a color value, use it as the accent color throughout
const accentColor = computed<string | null>(() => (hasColor.value && isOn.value ? color.value : null));

const lastUpdated = computed(() => (Object.keys(currentValues.value).length > 0 ? formatTime(new Date()) : ''));

// ── Switch-off timer / time-switch automations (same display logic as SwitchWidget) ──
const lightAutomations = computed<SwitchAutomationDisplay[]>(() => {
  const summaries = getSwitchWidgetAutomationSummaries(automationsStore.getDeviceAutomationSummaries(props.device.id));

  return summaries
    .map((automation) =>
      buildSwitchAutomationDisplay(
        automation,
        currentValues.value,
        new Date(nowTick.value),
        toSunTimesLike(solar.sunTimes.value),
      ),
    )
    .filter((automation): automation is SwitchAutomationDisplay => automation !== null);
});

/**
 * `nowTick` exists for two labels only: a running countdown (which prints
 * seconds) and a schedule label (which prints minutes). Writing it every second
 * re-rendered the whole pin once a second for text that could not have changed;
 * the tick now fires only when one of those two grains has actually passed.
 */
function advanceAutomationClock(): void {
  const now = Date.now();
  const minutePassed = Math.floor(now / 60_000) !== Math.floor(nowTick.value / 60_000);
  const countingDown = lightAutomations.value.some((automation) => automation.countdownText !== '');
  if (minutePassed || countingDown) nowTick.value = now;
}

async function refreshLightAutomations() {
  automationsStore.invalidate(props.device.id);
  await Promise.all([
    automationsStore.fetchDeviceAutomationSummaries(props.device.id, true),
    automationsStore.fetchDeviceAutomations(props.device.id, true),
  ]);
}

async function handleToggle() {
  await setProperty(props.device, 'onOff', !isOn.value);
}

/** The five brightness presets, in the chip row's own order (the sketch). */
const BRIGHTNESS_PRESETS = [10, 25, 50, 75, 100] as const;

/**
 * A trailing settle slot: `schedule` replaces the pending value and restarts
 * the timer, `flush` writes the pending value at once (a gesture ending), and
 * `dispose` drops it (unmount). The write waits for the pointer to be still for
 * `DRAG_SETTLE_MS` — the flood guard for the strip gestures.
 */
function createSettleSlot<T>(write: (value: T) => void) {
  let timer: number | null = null;
  let pending: { value: T } | null = null;

  function flush() {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    if (!pending) return;
    const { value } = pending;
    pending = null;
    write(value);
  }

  return {
    schedule(value: T) {
      pending = { value };
      if (timer !== null) clearTimeout(timer);
      timer = window.setTimeout(flush, DRAG_SETTLE_MS);
    },
    flush,
    dispose() {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
      pending = null;
    },
  };
}

const brightnessSettle = createSettleSlot<number>((value) => void setProperty(props.device, 'brightness', value));
const colorSettle = createSettleSlot<string>((value) => void setProperty(props.device, 'color', value));
const colorTempSettle = createSettleSlot<number>((value) => void setProperty(props.device, 'colorTemperature', value));

function setBrightness(pct: number) {
  brightnessSettle.schedule(pct);
}

// ── Press-and-hold dim (control-mode +/− buttons) ────────────────────────
const hasDimStep = computed(() => {
  if (Object.keys(currentValues.value).length > 0) return 'dim_step' in currentValues.value;
  return !!findValueByKeys(props.device, ['dim_step']);
});

let dimHoldTimer: number | null = null;
let dimHoldBrightness: number | null = null;
// Guards double-fire (pointer + mouse events for one press) and, when true,
// keeps the slider fill at duration-0 so rapid updates during a hold don't
// chase with the 150ms transition (flicker).
const dimHoldActive = ref(false);

function startDimStep(value: { decr_incr: number; data: number }) {
  setProperty(props.device, 'dim_step', value);
}
function stopDimStep() {
  setProperty(props.device, 'dim_step', { decr_incr: 0, data: 0 });
}

/** Press-and-hold dim: repeat the step command while held. For devices whose
 *  `dim_step` is a momentary start/stop this keeps it dimming; for devices
 *  whose `dim_step` is a single-step command (e.g. KNX DPT3) the repetition
 *  yields continuous stepping. Without a driver fallback: repeat brightness. */
function onDimHoldStart(direction: number, e?: PointerEvent) {
  if (dimHoldActive.value) return; // same physical press fires pointerdown AND mousedown
  dimHoldActive.value = true;
  // Capture the pointer: the button's active:scale-90 shrink (or small mouse
  // drift) would otherwise fire pointerleave/mouseleave and stop the dim
  // right after the first step — exactly the Electron hold regression.
  if (e && e.currentTarget) {
    try {
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    } catch {
      /* noop */
    }
  }
  if (hasDimStep.value) {
    const step = () => startDimStep({ decr_incr: direction, data: 1 });
    step();
    dimHoldTimer = window.setInterval(step, 150);
    return;
  }
  const step = brightnessStep.value;
  dimHoldBrightness = brightness.value ?? (isOn.value ? brightnessMax.value : brightnessMin.value);
  const apply = () => {
    const cur = dimHoldBrightness ?? brightness.value ?? brightnessMin.value;
    const next = Math.max(
      brightnessMin.value,
      Math.min(brightnessMax.value, Math.round((cur + (direction === 1 ? step : -step)) / step) * step),
    );
    dimHoldBrightness = next;
    setProperty(props.device, 'brightness', next);
  };
  apply();
  dimHoldTimer = window.setInterval(apply, 150);
}

function onDimHoldEnd() {
  if (!dimHoldActive.value) return; // already ended (pointerup + mouseup both fire for one release)
  dimHoldActive.value = false;
  dimHoldBrightness = null;
  if (dimHoldTimer !== null) {
    clearInterval(dimHoldTimer);
    dimHoldTimer = null;
  }
  if (hasDimStep.value) stopDimStep();
}

/** The brightness bars (button + grid): the fill follows the finger locally and
 *  the value is written once the pointer pauses or is released. */
function onBrightBarDown(e: PointerEvent) {
  try {
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  } catch {
    /* noop */
  }
  activeDrag.value = 'brightness';
  applyBrightBar(e);
}
function onBrightBarMove(e: PointerEvent) {
  if (activeDrag.value !== 'brightness') return;
  applyBrightBar(e);
}
function onBrightBarUp() {
  if (activeDrag.value !== 'brightness') return;
  activeDrag.value = null;
  brightnessSettle.flush();
}
function applyBrightBar(e: PointerEvent) {
  const el = e.currentTarget as HTMLElement;
  const rect = el.getBoundingClientRect();
  const pct = Math.max(0, Math.min(100, Math.round(((e.clientX - rect.left) / rect.width) * 100)));
  brightnessOverlay.hold(pct);
  setBrightness(pct);
}

function openColorPicker() {
  colorInputRef.value?.click();
}

function handleColorChange(event: Event) {
  const value = (event.target as HTMLInputElement).value;
  colorOverlay.hold(value);
  // Replace any pending drag value, then write at once — the picker is a
  // one-shot and must not be overwritten by a stale settle a moment later.
  colorSettle.schedule(value);
  colorSettle.flush();
}

/** The strips read the held value first, so a slow (or out-of-order) device
 *  echo cannot drag the thumb back mid-gesture. */
const shownColor = computed(() => colorOverlay.held.value ?? color.value);
const shownColorTemp = computed(() => colorTempOverlay.held.value ?? colorTemperature.value);
const colorTempPct = computed(() => ((shownColorTemp.value - 2000) / (6500 - 2000)) * 100);

/** Current hue 0-360 derived from the shown color, to position the thumb (0-100%) */
const colorHuePct = computed(() => {
  const hex = shownColor.value.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  if (max === min) return 0;
  const d = max - min;
  let h = 0;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return h * 100;
});

function hueFromClientX(clientX: number): string {
  const el = hueBarRef.value;
  if (!el) return color.value;
  const rect = el.getBoundingClientRect();
  const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
  const hue = Math.round(pct * 360);
  // Convert HSL (full sat/light) to hex
  const l = 0.5,
    s = 1;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + hue / 30) % 12;
    const c = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(c * 255)
      .toString(16)
      .padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

// The two colour strips share one gesture shape: pointer capture owns the drag,
// the local overlay tracks the finger, and the settle slot writes only when the
// finger pauses (or on release). Pointer events cover mouse and touch alike, so
// the old mouse/touch pairs — and the document listeners they left behind — go.
function onHueDown(e: PointerEvent) {
  try {
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  } catch {
    /* noop */
  }
  activeDrag.value = 'color';
  applyHueDrag(e.clientX);
}
function onHueMove(e: PointerEvent) {
  if (activeDrag.value !== 'color') return;
  applyHueDrag(e.clientX);
}
function onHueUp() {
  if (activeDrag.value !== 'color') return;
  activeDrag.value = null;
  colorSettle.flush();
}
function applyHueDrag(clientX: number) {
  const hex = hueFromClientX(clientX);
  colorOverlay.hold(hex);
  colorSettle.schedule(hex);
}

function onTempDown(e: PointerEvent) {
  try {
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  } catch {
    /* noop */
  }
  activeDrag.value = 'temp';
  applyTempDrag(e.clientX, e.currentTarget as HTMLElement);
}
function onTempMove(e: PointerEvent) {
  if (activeDrag.value !== 'temp') return;
  applyTempDrag(e.clientX, e.currentTarget as HTMLElement);
}
function onTempUp() {
  if (activeDrag.value !== 'temp') return;
  activeDrag.value = null;
  colorTempSettle.flush();
}
function applyTempDrag(clientX: number, el: HTMLElement) {
  const rect = el.getBoundingClientRect();
  const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
  const temp = Math.round(2000 + pct * (6500 - 2000));
  colorTempOverlay.hold(temp);
  colorTempSettle.schedule(temp);
}

function findValueByTrait(device: Device, trait: string): DeviceValue | undefined {
  return device.deviceValues?.find((v) => {
    const name = (v.name || '').toLowerCase();
    const key = (v.valueKey || '').toLowerCase();
    return name.includes(trait.toLowerCase()) || key.includes(trait.toLowerCase());
  });
}

// ── Display ──────────────────────────────────────────────────────────────
const displayName = computed(() =>
  props.displayMode === 'full' ? props.device.deviceName : props.device.shortName || props.device.deviceName,
);
const sizeClass = computed(() => (props.fluid ? 'device-icon-pin--fluid' : `device-icon-pin--${props.size ?? 'md'}`));

/**
 * The pin's own icon-mode fold (P13): the ring carries the light's level — `0`
 * while it is off, the reported brightness while it is on, and a lamp that
 * reports no level at all is all the way up.
 */
const ringValue = computed(() => (isOn.value ? (brightness.value ?? 100) : 0));

/** The drawn icon's size in the pin, in the same steps `GenericIcon` used. */
const pinIconSize = computed(() => {
  if (props.fluid) return '28px';
  switch (props.size) {
    case 'lg':
      return '28px';
    case 'sm':
      return '16px';
    default:
      return '22px';
  }
});
</script>

<style scoped>
/* ── Button mode ── */
/* The button sits in the dashboard's vertical scroller (card t_fcaa5f33): keep the
   vertical pan for the browser, so it stops claiming the press over a sideways
   drift — claiming it cancels the pointer and eats the tap. */
.device-button {
  touch-action: pan-y;
}

/* Stacked layout: head row on top, brightness controls pinned to the bottom.
   Devices with a thumbnail skip the icon and leave the middle for the image. */
.light-button-layout {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  width: 100%;
  height: 100%;
  margin-top: 0;
  min-height: 0;
}

.light-button-layout--plain {
  justify-content: center;
}

.device-button-head {
  display: flex;
  align-items: center;
  gap: 0.55em;
  min-width: 0;
}

/* Name above the value, beside the circle */
.device-button-text {
  display: flex;
  flex-direction: column;
  gap: 0.1em;
  min-width: 0;
}

/* Smaller than the value, and kept to a single line (ellipsis when too long) */
.light-button-layout .device-name {
  font-size: 0.62em;
  min-width: 0;
}

.device-icon--circle {
  width: 2.35em;
  height: 2.35em;
  margin-bottom: 0;
  border-radius: 50%;
  /* The circle's fill is the neutral canvas, never the ambient tint: a lamp's
     badge shows the lamp, not the house's colour. */
  --icon-circle-fill: var(--light-control-fill);
  background: var(--icon-circle-fill);
  border: 1px solid rgba(148, 163, 184, 0.35);
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

.light-on .device-icon--circle {
  background: color-mix(in srgb, var(--device-color-light, rgb(250, 204, 21)) 14%, var(--icon-circle-fill));
  border-color: color-mix(in srgb, var(--device-color-light, rgb(250, 204, 21)) 45%, transparent);
  box-shadow:
    0 0 10px color-mix(in srgb, var(--device-color-light, rgb(250, 204, 21)) 30%, transparent),
    0 0 24px color-mix(in srgb, var(--device-color-light, rgb(250, 204, 21)) 12%, transparent);
}

/* The circle toggles the power on its own, so it advertises itself as clickable */
.light-power-circle {
  cursor: pointer;
}
/* The lamp's badge keeps its own dark fill whatever the house is wearing: the
   ambient tint belongs to the surfaces around it, not to the lamp's own control. */
.device-icon-pin.light-icon-pin {
  background-color: var(--light-pin-fill);
  border-color: var(--light-pin-border);
}
/* The picture keeps clear of the ring's arc: the arc's inner edge sits at 79 %
   of the pin's box (r=39.5 on `ControlStatusRing`'s 100 box), so the frame takes
   a step less than that and stays centred in the ring's own content slot. */
.device-icon-pin__thumb {
  width: 72%;
  height: 72%;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}
.device-icon-pin__img {
  width: 100%;
  height: 100%;
  /* The pin is an icon frame, not a photo crop (owner, 25.10.2026). */
  object-fit: contain;
  object-position: center;
  transition: filter 0.3s ease;
}
.device-icon-pin__img--grayscale {
  filter: grayscale(100%) brightness(0.6);
}
.device-icon-pin__img--lit {
  filter: brightness(1.1) saturate(1.2);
}

.light-on .device-icon span,
.light-on .device-name,
.light-on .device-status {
  color: var(--device-color-light) !important;
}
.light-on .device-current-value {
  color: var(--device-color-light) !important;
}
.light-on {
  border-color: var(--device-color-light) !important;
  box-shadow:
    0 0 10px color-mix(in srgb, var(--device-color-light) 30%, transparent),
    0 0 24px color-mix(in srgb, var(--device-color-light) 10%, transparent);
}

.light-button--with-brightness:hover {
  transform: none !important;
  box-shadow: inherit !important;
}

/* Device photo fills the icon circle in the head row */
.device-icon--circle :deep(.device-thumb) {
  width: 100%;
  height: 100%;
}
.device-current-value {
  font-size: var(--tile-status-size);
  font-weight: 700;
  line-height: 1;
  color: rgb(17, 24, 39);
}

.device-button-bright-bar {
  width: 100%;
  height: 1.35em;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 4px;
  overflow: hidden;
  position: relative;
  flex-shrink: 0;
  margin-top: 0.2em;
  cursor: pointer;
  touch-action: none;
  border: 1px solid rgba(255, 255, 255, 0.1);
}
.device-button-bright-fill {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  background: var(--device-color-light, rgb(250, 204, 21));
  border-radius: 0;
  transition: width 0.15s ease-out;
  opacity: 0.85;
}
.device-button-bright-fill--off {
  opacity: 0.3;
  filter: grayscale(0.7);
}
/* While the user drags, the fill tracks the finger directly — the width easing
   would lag behind and read as a stutter. */
.device-button-bright-fill--dragging {
  transition: none;
}
.device-button-bright-label {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.65rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.9);
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);
  pointer-events: none;
  letter-spacing: 0.03em;
}

/* Dim −/+ controls flanking the brightness bar (like climate temp buttons) */
.light-dim-controls {
  display: flex;
  align-items: center;
  gap: 0.35em;
  margin-top: auto;
  padding-top: 0.45em;
}
.light-dim-button {
  width: 1.45em;
  height: 1.45em;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: color-mix(in srgb, var(--device-color-light, rgb(250, 204, 21)) 12%, transparent);
  color: var(--device-color-light, rgb(250, 204, 21));
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    opacity 0.15s ease,
    transform 0.15s ease;
}
.light-dim-button:hover:not(:disabled) {
  background: color-mix(in srgb, var(--device-color-light, rgb(250, 204, 21)) 20%, transparent);
}
/* Dim buttons go gray when the light is off */
.light-off .light-dim-button {
  background: color-mix(in srgb, rgb(148, 163, 184) 14%, transparent);
  color: rgb(148, 163, 184);
}
.light-off .light-dim-button:hover:not(:disabled) {
  background: color-mix(in srgb, rgb(148, 163, 184) 24%, transparent);
}
.light-dim-button .material-symbols-outlined {
  font-size: 1rem;
}
.light-dim-bar {
  flex: 1 1 auto;
  min-width: 0;
  margin-top: 0;
}

/* ── Control mode ── */
.preset-button {
  min-height: 32px;
  min-width: 80px;
  padding: 0.375rem 0.75rem;
  background: transparent;
  color: rgb(55 65 81);
  border-radius: 0.5rem;
  transition: all 0.2s;
  font-weight: normal;
  border-right: 4px solid rgb(209 213 219);
  text-align: right;
}
.preset-button:hover:not(:disabled) {
  background: rgb(254 249 195);
  border-color: rgb(250 204 21);
}
.preset-button:active:not(:disabled) {
  background: rgb(254 240 138);
}
.preset-button:disabled {
  opacity: 0.5;
}
.preset-button-active {
  border-color: rgb(250 204 21) !important;
  background: rgb(254 240 138) !important;
  font-weight: 600;
  color: rgb(133 77 14);
}

/* The app themes by the `.dark` class on <html>, so these read that and not the
   OS preference — an OS-dark, app-light setup must not paint dark text. */
.dark .device-current-value {
  color: rgb(255, 255, 255);
}
.dark .preset-button {
  color: rgb(209 213 219);
  border-color: rgb(75 85 99);
}
.dark .preset-button:hover:not(:disabled) {
  background: rgba(250, 204, 21, 0.2);
  border-color: rgb(234 179 8);
}
.dark .preset-button-active {
  border-color: rgb(234 179 8) !important;
  background: rgba(250, 204, 21, 0.2) !important;
  color: rgb(253 224 71);
}

@keyframes glow-pulse {
  0%,
  100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}
.device-icon-pin__glow {
  position: absolute;
  inset: -16px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(250, 204, 21, 0.55) 0%, rgba(250, 204, 21, 0.22) 45%, transparent 72%);
  pointer-events: none;
  animation: glow-pulse 3s ease-in-out infinite;
}

/* ── Control mode: the sheet body ───────────────────────────────────────── */
/*
 * The light sheet in the control-sheet language: the ring-dial hero, the
 * preset chips, the labelled strips and the sheet's field/select/meta rows.
 * Every accent these styles read is `--sheet-accent` — the body root sets it
 * to the light's colour while the light is lit and leaves the host's accent
 * (the chrome's, i.e. `--device-color-light`) otherwise.
 */
.light-sheet {
  display: flex;
  flex-direction: column;
  gap: calc(1rem * var(--ui-space-factor));
  min-width: 0;
}

/* The hero: the dial primitive's own 12.25rem size + the sketch's caption. */
.light-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding-top: 0.25rem;
}

.light-hero-caption {
  font-size: 0.6875rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgb(var(--gray-500));
  text-align: center;
}

.dark .light-hero-caption {
  color: rgb(var(--gray-400));
}

/* The momentary dim pair (`dim_step` devices): round buttons in the stepper's
   shape — the hold belongs to them, so the sheet must not scroll from here. */
.light-dimstep {
  display: flex;
  justify-content: center;
  gap: calc(0.75rem * var(--ui-space-factor));
}

.light-dimstep-btn {
  width: 2.5rem;
  height: 2.5rem;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1.5px solid var(--glass-edge);
  background: rgba(148, 163, 184, 0.1);
  color: rgb(var(--gray-600));
  cursor: pointer;
  touch-action: none;
  transition:
    background 0.15s,
    border-color 0.15s,
    color 0.15s,
    transform 0.1s;
}

.dark .light-dimstep-btn {
  background: rgba(255, 255, 255, 0.03);
  color: rgb(var(--gray-300));
}

.light-dimstep-btn--accent {
  border-color: color-mix(in srgb, var(--sheet-accent) 55%, transparent);
  background: color-mix(in srgb, var(--sheet-accent) 13%, transparent);
  color: var(--sheet-accent);
}

.light-dimstep-btn:active {
  transform: scale(0.94);
}

/* The five preset chips, centred like the sketch's row. */
.light-presets {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: calc(0.5rem * var(--ui-space-factor));
}

/* A labelled sheet row (temperature, colour, WLED fields). */
.light-row {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  min-width: 0;
  transition: opacity 0.2s;
}

.light-row--off {
  opacity: 0.5;
}

.light-row--wled {
  gap: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--glass-edge-soft);
}

.light-row-head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  font-size: 0.8125rem;
}

.light-row-key {
  font-weight: 550;
  color: rgb(var(--gray-500));
}

.dark .light-row-key {
  color: rgb(var(--gray-400));
}

.light-row-value {
  margin-left: auto;
  font-weight: 650;
  color: rgb(var(--gray-800));
  font-variant-numeric: tabular-nums;
}

.dark .light-row-value {
  color: rgb(var(--gray-100));
}

.light-row-value--mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.7813rem;
}

.light-row-ends {
  display: flex;
  justify-content: space-between;
  font-size: 0.6875rem;
  color: rgb(var(--gray-400));
}

.dark .light-row-ends {
  color: rgb(var(--gray-500));
}

/* The gradient strip (colour temperature, hue) — a thin bar with the sketch's
   white thumb riding it. `touch-action: none` mirrors the old strips: a drag
   that starts on the bar owns the gesture. */
.light-strip {
  position: relative;
  height: 0.875rem;
  margin: 0.375rem 0.125rem 0.25rem;
  border: 1px solid var(--glass-edge);
  border-radius: 999px;
  cursor: pointer;
  touch-action: none;
}

.light-strip--hue {
  cursor: crosshair;
}

.light-strip--disabled {
  opacity: 0.5;
  pointer-events: none;
}

.light-strip-thumb {
  position: absolute;
  top: 50%;
  width: 1.25rem;
  height: 1.25rem;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  background: #fff;
  border: 2px solid rgba(255, 255, 255, 0.85);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.4);
  pointer-events: none;
  transition: left 0.1s;
}

/* The hue thumb carries the colour the strip is at. */
.light-strip-thumb--tinted {
  transition:
    left 0.1s,
    background-color 0.15s;
}

/* Mid-drag the thumb must sit exactly under the finger; the easing above is
   only there for changes that arrive from the device. */
.light-strip--dragging .light-strip-thumb {
  transition: none;
}

/* The sheet's small round icon button (the colour row's palette). */
.light-icon-btn {
  width: 1.75rem;
  height: 1.75rem;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--glass-edge);
  border-radius: 50%;
  background: rgba(148, 163, 184, 0.1);
  color: rgb(var(--gray-500));
  cursor: pointer;
  transition:
    background 0.15s,
    color 0.15s;
}

.light-icon-btn:hover:not(:disabled) {
  background: rgba(148, 163, 184, 0.2);
  color: rgb(var(--gray-700));
}

.dark .light-icon-btn {
  background: rgba(255, 255, 255, 0.04);
  color: rgb(var(--gray-300));
}

.light-icon-btn:disabled {
  opacity: 0.5;
  cursor: default;
}

/* WLED fields: the sheet's label-over-select pair. */
.light-field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
}

.light-field-label {
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgb(var(--gray-500));
}

.dark .light-field-label {
  color: rgb(var(--gray-400));
}

.light-select {
  width: 100%;
  font: inherit;
  font-size: 0.8125rem;
  padding: calc(0.5rem * var(--ui-space-factor)) calc(0.75rem * var(--ui-space-factor));
  border: 1px solid var(--glass-edge);
  border-radius: calc(0.625rem * var(--ui-radius-factor));
  background: rgba(148, 163, 184, 0.1);
  color: rgb(var(--gray-800));
  cursor: pointer;
}

.dark .light-select {
  background: rgba(255, 255, 255, 0.04);
  color: rgb(var(--gray-100));
}

.light-select:focus-visible {
  outline: 2px solid color-mix(in srgb, var(--sheet-accent) 60%, transparent);
  outline-offset: 1px;
}

.light-select:disabled {
  opacity: 0.5;
}

.light-wled-empty {
  margin: 0;
  text-align: center;
  font-size: 0.75rem;
  color: rgb(var(--gray-500));
}

/* The meta row: last updated on the left, over the sheet's hairline. */
.light-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding-top: 0.375rem;
  border-top: 1px solid var(--glass-edge-soft);
  font-size: 0.71875rem;
  color: rgb(var(--gray-500));
}

.dark .light-meta {
  color: rgb(var(--gray-400));
}

.light-meta-left {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
}

/* ── Card face (the grid the lights page draws) ─────────────────────────────
   The shell owns the frame, the name and the size rules; these are the light's
   own parts inside it: the status picture, the reading and the dim row. */
.light-card-face {
  --device-color-light: rgb(250, 204, 21);
}

.light-card-face__img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition:
    filter 0.2s ease,
    opacity 0.2s ease;
}

/* Off: the picture is there, just unlit. */
.light-card-face__img--off {
  opacity: 0.55;
  filter: grayscale(0.75);
}

.light-card-face__glyph {
  font-size: 2.4rem;
}

.light-card-face__value {
  flex: none;
  text-align: center;
  font-size: var(--card-value, 0.85rem);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: #64748b;
}

.dark .light-card-face__value {
  color: #94a3b8;
}

.light-card-face.light-on .light-card-face__value {
  color: color-mix(in srgb, var(--device-color-light) 55%, #111827);
}

.dark .light-card-face.light-on .light-card-face__value {
  color: color-mix(in srgb, var(--device-color-light) 60%, #f8fafc);
}

/* The dim row sits on the card's own padding, not the button's — and its bar
   takes whatever the two buttons leave, rather than claiming the full width. */
.light-card-face__dim {
  width: 100%;
  flex: 1 1 auto;
  padding-top: 0.1em;
}

.light-card-face__dim .light-dim-bar {
  width: auto;
  flex: 1 1 auto;
  min-width: 0;
}
</style>
